/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import React from "react";

import { usePlayerComponents } from "../../../shared/context/PlayerComponentsContext";
import { usePlayerTranslation } from "../../../shared/context/PlayerTranslationContext";
import PlayIcon from "../../../shared/icons/play.svg";
import ReplayIcon from "../../../shared/icons/replay.svg";

import "./ContinueWatchingOverlay.scss";

interface ContinueWatchingOverlayProps {
  isVisible: boolean;
  imageUrl?: string;
  /**
   * If timeout is `undefined`, overlay will be displayed until user choose option.
   */
  timeoutInSeconds?: number;
  onPositiveClick?: () => void;
  onNegativeClick?: () => void;
}

export const ContinueWatchingOverlay = (
  props: ContinueWatchingOverlayProps,
) => {
  const {
    isVisible,
    imageUrl,
    timeoutInSeconds,
    onPositiveClick,
    onNegativeClick,
  } = props;
  const { t } = usePlayerTranslation();
  const { Button } = usePlayerComponents();

  const [timeLeftInSeconds, setTimeLeftInSeconds] = React.useState(
    timeoutInSeconds ?? -1,
  );
  const intervalRef = React.useRef<number>(-1);

  const stopTimer = React.useCallback(() => {
    if (intervalRef.current !== -1) {
      clearInterval(intervalRef.current);
      intervalRef.current = -1;
    }
  }, []);

  const handlePositiveClick = React.useCallback(() => {
    stopTimer();
    onPositiveClick?.();
  }, [stopTimer, onPositiveClick]);

  const handleNegativeClick = React.useCallback(() => {
    stopTimer();
    onNegativeClick?.();
  }, [stopTimer, onNegativeClick]);

  React.useEffect(() => {
    if (timeoutInSeconds && intervalRef.current === -1) {
      intervalRef.current = window.setInterval(() => {
        setTimeLeftInSeconds((prev) => {
          if (prev <= 0) {
            return prev;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      stopTimer();
    };
  }, [timeoutInSeconds, stopTimer]);

  React.useEffect(() => {
    if (timeLeftInSeconds <= 0 && timeoutInSeconds) {
      stopTimer();
      handlePositiveClick();
    }
  }, [timeLeftInSeconds, timeoutInSeconds, stopTimer, handlePositiveClick]);

  const isTimeoutSet = timeoutInSeconds != undefined;

  return isVisible ? (
    <div className={"ContinueWatchingOverlay"}>
      <div className={"ContinueWatchingOverlay__Popup"}>
        <img
          className={"ContinueWatchingOverlay__Image"}
          src={imageUrl}
          alt="overlay"
        />
        <div className={"ContinueWatchingOverlay__Footer"}>
          <Button onClick={handleNegativeClick} variant="primary">
            <PlayIcon aria-hidden="true" focusable="false" />
            {t("WATCH__START_FROM_BEGINNING")}
          </Button>
          <Button onClick={handlePositiveClick} variant="primary">
            {!isTimeoutSet && (
              <ReplayIcon aria-hidden="true" focusable="false" />
            )}
            {isTimeoutSet && `(${timeLeftInSeconds}s) `}
            {t("WATCH__CONTINUE_WATCHING")}
          </Button>
        </div>
      </div>
    </div>
  ) : null;
};
